import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerTypeHint,
} from "@capacitor/barcode-scanner";

/**
 * The in-app scanner (`#269`), native only — `screens/home.tsx` renders the
 * button behind `Capacitor.isNativePlatform()`.
 *
 * A bare code is letters, digits and one optional dash, the shape
 * `apps/pos/src/screens/sign-in.tsx`'s `groupUserCode` displays and a person
 * types by hand (`staff.home.enterCodePlaceholder`). Any host is accepted for
 * the `/approve` link, because this app does not know its own public origin
 * at runtime — a foreign QR can at most pre-fill a code, and `approve.tsx`
 * still makes the person confirm the till's own request.
 */
const BARE_CODE = /^[A-Za-z0-9]+(-[A-Za-z0-9]+)?$/;

export function userCodeFromScan(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;

  try {
    const url = new URL(trimmed);
    if (url.pathname !== "/approve") return null;
    const userCode = url.searchParams.get("user_code");
    return userCode !== null && userCode.trim() !== "" ? userCode : null;
  } catch {
    // Not a URL at all — fall through to the bare-code shape.
  }

  return BARE_CODE.test(trimmed) ? trimmed : null;
}

/**
 * The plugin rejects a user's own cancel the same way it rejects a camera
 * failure — a thrown error, not a distinct result — so cancellation is told
 * apart by what that error says. `OS-PLUG-BARC-0006` is
 * `CapacitorBarcodeScannerPlugin`'s own `scanningCancelled` code on iOS
 * (`node_modules/@capacitor/barcode-scanner/ios/.../OSBarcodeError.swift`)
 * and its web fallback rejects with the same message
 * (`.../dist/esm/web.js`); Android's own `OSBARCController` error is read the
 * same way rather than assumed identical.
 */
function isScanCancelled(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if ((error as { code?: string }).code === "OS-PLUG-BARC-0006") return true;
  return /cancel/i.test(error.message);
}

export async function scanTillCode(): Promise<string | null | "cancelled"> {
  try {
    const { ScanResult } = await CapacitorBarcodeScanner.scanBarcode({
      hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
    });
    return userCodeFromScan(ScanResult);
  } catch (error) {
    return isScanCancelled(error) ? "cancelled" : null;
  }
}
